import { useState } from "react";
import { toast } from "sonner";
import { Gavel, Plus, Loader2, Sparkles, FileText, Pencil, Trash2, CalendarClock, ClipboardCheck, Lightbulb, BrainCircuit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, PageState, Empty, UpgradeGate } from "@/components/app/Common";
import { SimpleSelect } from "@/components/app/SimpleSelect";
import { DecisionDialog, CheckinDialog, STATUS, CATEGORIES, RATINGS } from "@/components/app/DecisionDialogs";
import { PlaybookPanel, Helpful } from "@/components/app/Playbook";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg, openFile } from "@/lib/api";
import { fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

const VERDICT = { on_track: ["On track", "text-emerald-600 dark:text-emerald-400"], mixed: ["Mixed results", "text-amber-600 dark:text-amber-400"],
  off_track: ["Off track", "text-red-500"], too_early: ["Too early to tell", "text-muted-foreground"] };
const CONF = { high: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400", medium: "bg-amber-500/12 text-amber-600 dark:text-amber-400", low: "bg-slate-500/15 text-slate-600 dark:text-slate-300" };
const cat = (k) => CATEGORIES.find((c) => c[0] === k)?.[1] || "Other";

const Review = ({ r }) => (
  <div className="mt-3 rounded-lg border border-primary/25 bg-primary/[0.04] p-3 text-sm" data-testid="decision-review">
    <div className="flex flex-wrap items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><b className={VERDICT[r.verdict][1]} data-testid="decision-review-verdict">{VERDICT[r.verdict][0]}</b><span className="text-xs text-muted-foreground">· Claude checked {fmtDate(r.at)}</span></div>
    <p className="mt-1">{r.summary}</p>
    {r.evidence?.length > 0 && <ul className="mt-1.5 list-disc pl-5 text-xs text-muted-foreground space-y-0.5">{r.evidence.map((e) => <li key={e}>{e}</li>)}</ul>}
    {r.next_step && <p className="mt-1.5 text-xs"><b>Next step:</b> {r.next_step}</p>}
  </div>
);

const Checkins = ({ d }) => (d.checkins?.length ? (
  <div className="mt-3 border-l-2 border-border pl-3 space-y-2" data-testid={`decision-checkins-${d.id}`}>
    {d.checkins.slice(-3).reverse().map((c) => (
      <div key={c.at} className="text-sm"><span className={`font-semibold ${RATINGS[c.rating][1]}`}>{RATINGS[c.rating][0]}</span><span className="text-xs text-muted-foreground"> · {fmtDate(c.at)} · {c.by}{c.via === "email" && " (via email)"}</span>
        {c.actual_result && <div className="text-muted-foreground">{c.actual_result}</div>}
        {c.lessons && <div className="text-xs mt-0.5 flex gap-1"><Lightbulb className="h-3.5 w-3.5 text-amber-500 shrink-0" />{c.lessons}</div>}</div>))}
  </div>) : null);

const Dates = ({ d }) => (d.review_dates?.length ? (
  <div className="flex flex-wrap items-center gap-1.5 mt-3 text-[11px]" data-testid={`decision-schedule-${d.id}`}>
    <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
    {d.review_dates.map((x) => { const done = d.reviews_done?.includes(x); return <span key={x} className={`rounded-md px-1.5 py-0.5 ${done ? "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400 line-through" : "bg-muted text-muted-foreground"}`}>{fmtDate(x)}</span>; })}
  </div>) : null);

const DecisionCard = ({ d, onEdit, onCheckin, reload, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/decisions/${d.id}/review`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm(`Delete “${d.title}”?`)) return;
    await api.delete(`/decisions/${d.id}`);
    reload();
  };
  const last = d.reviews?.[d.reviews.length - 1];
  return (
    <div className="surface p-5" data-testid={`decision-card-${d.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex flex-wrap items-center gap-2"><span className="font-semibold">{d.title}</span>
            <span className="text-[11px] rounded-full px-2 py-0.5 border border-border text-muted-foreground" data-testid={`decision-category-${d.id}`}>{cat(d.category)}</span>
            <span className={`text-[11px] rounded-full px-2 py-0.5 ${STATUS[d.status][1]}`} data-testid={`decision-status-${d.id}`}>{STATUS[d.status][0]}</span>
            {d.outcome_rating && <span className={`text-[11px] font-semibold ${RATINGS[d.outcome_rating][1]}`} data-testid={`decision-rating-${d.id}`}>{RATINGS[d.outcome_rating][0]}</span>}
            {d.review_due && <span className="text-[11px] rounded-full px-2 py-0.5 bg-amber-500/12 text-amber-600 dark:text-amber-400" data-testid={`decision-review-due-${d.id}`}>Review due</span>}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(d.decided_at)} · {d.decided_by || "—"}{d.cost != null && ` · ${Number(d.cost).toLocaleString()} invested`}{d.entity_name && ` · ${d.entity_name}`}{d.related_name && ` · ${d.related_name}`}</div>
        </div>
        <div className="flex flex-wrap gap-1">
          {canEdit && <Button size="sm" onClick={() => onCheckin(d)} data-testid={`decision-checkin-btn-${d.id}`}><ClipboardCheck className="h-3.5 w-3.5 mr-1" />Log results</Button>}
          <Button size="sm" variant="outline" onClick={review} disabled={busy} data-testid={`decision-review-btn-${d.id}`}>{busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Check outcome</Button>
          {canEdit && <Button size="icon" variant="ghost" onClick={() => onEdit(d)} aria-label="Edit" data-testid={`decision-edit-${d.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
          {canEdit && <Button size="icon" variant="ghost" onClick={remove} aria-label="Delete" data-testid={`decision-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
        </div>
      </div>
      {d.decision && <p className="text-sm mt-2">{d.decision}</p>}
      <div className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        {d.rationale && <div><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Why</div>{d.rationale}</div>}
        {d.expected_outcome && <div><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Expected outcome</div>{d.expected_outcome}</div>}
      </div>
      <Dates d={d} />
      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
        {d.tasks.total > 0 && <span className="text-muted-foreground" data-testid={`decision-tasks-${d.id}`}>{d.tasks.done}/{d.tasks.total} follow-through tasks done</span>}
        {d.documents.map((x) => <button key={x.id} onClick={() => openFile(`/documents/${x.id}/versions/${x.version}/file`).catch(() => toast.error("Could not open file"))} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`decision-doc-link-${x.id}`}><FileText className="h-3.5 w-3.5" />{x.title}</button>)}
      </div>
      <Checkins d={d} />
      {last && <Review r={last} />}
    </div>
  );
};

const Patterns = () => {
  const state = useLoad(() => api.get("/decisions/insights").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/decisions/insights");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const p = state.data;
  return (
    <div className="surface p-6 mb-6 border-l-4 border-primary/50" data-testid="decision-patterns">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 font-semibold"><BrainCircuit className="h-4 w-4 text-primary" />What your decisions teach you
          {p && <span className="text-xs font-normal text-muted-foreground">· {p.reviewed} of {p.based_on} reviewed · {fmtDate(p.created_at)}</span>}</div>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="decision-patterns-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}{p ? "Refresh patterns" : "Find patterns"}</Button>
      </div>
      {!p ? <p className="text-sm text-muted-foreground mt-2">Claude reads every decision, outcome and lesson to find what tends to work for your business — pricing, hiring, equipment, acquisitions and more.</p> : (
        <div className="mt-3">
          {p.summary && <p className="text-sm" data-testid="decision-patterns-summary">{p.summary}</p>}
          {p.patterns.length > 0 && <div className="grid gap-3 md:grid-cols-2 mt-4">{p.patterns.map((x, i) => (
            <div key={x.title} className="rounded-lg border border-border p-3.5" data-testid={`decision-pattern-${i}`}>
              <div className="flex flex-wrap items-center gap-2"><b className="text-sm">{x.title}</b><span className="text-[11px] text-muted-foreground">{cat(x.category)}</span><span className={`text-[11px] rounded-full px-2 py-0.5 ${CONF[x.confidence]}`}>{x.confidence} confidence</span></div>
              <p className="text-sm text-muted-foreground mt-1">{x.insight}</p>
              {x.decisions.length > 0 && <p className="text-[11px] text-muted-foreground mt-1.5">Based on: {x.decisions.map((d) => d.title).join(" · ")}</p>}
            </div>))}</div>}
          {p.lessons.length > 0 && <div className="mt-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Rules of thumb</div>
            <ul className="space-y-1 text-sm" data-testid="decision-lessons">{p.lessons.map((l) => <li key={l} className="flex gap-2"><Lightbulb className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />{l}</li>)}</ul></div>}
          {p.gaps.length > 0 && <p className="text-xs text-muted-foreground mt-3">To sharpen this: {p.gaps.join(" ")}</p>}
          <div className="mt-3"><Helpful feature="decision_insights" itemId={p.created_at} /></div>
        </div>)}
    </div>
  );
};

export default function Decisions() {
  const { user } = useAuth();
  const canEdit = user?.workspace?.role !== "viewer";
  const owner = user?.workspace?.role === "owner";
  const state = useLoad(() => Promise.all([api.get("/decisions"), api.get("/entities")]).then(([a, b]) => ({ ...a.data, ents: b.data })));
  const [edit, setEdit] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("");
  const [tab, setTab] = useState("decisions");
  if (state.gated) return <div><PageHeader eyebrow="AI Chief of Staff" title="Decision Intelligence" /><UpgradeGate gated={state.gated} /></div>;
  const all = state.data?.decisions || [];
  const items = all.filter((d) => (filter === "all" || (filter === "due" ? d.review_due : d.status === filter)) && (!category || d.category === category));
  const rated = all.filter((d) => d.outcome_rating);
  const hit = rated.filter((d) => ["exceeded", "met"].includes(d.outcome_rating)).length;
  return (
    <div>
      <PageHeader eyebrow="AI Chief of Staff" title="Decision Intelligence" subtitle="Log major calls, schedule reviews, capture what actually happened — and let Claude turn it into institutional memory.">
        {canEdit && <Button onClick={() => setEdit({})} data-testid="decision-new-btn"><Plus className="h-4 w-4 mr-1.5" />Record decision</Button>}
      </PageHeader>
      <div className="inline-flex rounded-lg border border-border p-1 mb-6" data-testid="decision-tabs">
        {[["decisions", "Decisions"], ["playbook", "Playbook"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`decision-tab-${k}`} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>))}
      </div>
      {tab === "playbook" ? <PlaybookPanel owner={owner} /> : <PageState state={state}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[["Decisions logged", all.length, "decision-stat-total"], ["Reviews due", all.filter((d) => d.review_due).length, "decision-stat-due"],
            ["Outcomes captured", rated.length, "decision-stat-rated"], ["Met or beat expectations", rated.length ? `${Math.round((hit / rated.length) * 100)}%` : "—", "decision-stat-hit"]].map(([l, v, id]) => (
            <div key={id} className="surface p-4"><div className="text-xs text-muted-foreground">{l}</div><div className="text-2xl font-semibold font-mono mt-1" data-testid={id}>{v}</div></div>))}
        </div>
        {owner && all.length > 0 && <Patterns />}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="decision-filters">
          {[["all", "All"], ["due", "Review due"], ...Object.entries(STATUS).map(([k, [l]]) => [k, l])].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`decision-filter-${k}`}
              className={`text-xs rounded-full border px-3 py-1 transition-colors ${filter === k ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>))}
          <div className="w-44 ml-auto"><SimpleSelect value={category} onChange={setCategory} options={CATEGORIES.map(([value, label]) => ({ value, label }))} allowNone noneLabel="All categories" testId="decision-category-filter" /></div>
        </div>
        {items.length ? <div className="space-y-4">{items.map((d) => <DecisionCard key={d.id} d={d} onEdit={setEdit} onCheckin={setCheckin} reload={state.reload} canEdit={canEdit} />)}</div>
          : <Empty icon={Gavel} title="No decisions here yet" text="Record important calls — price changes, hires, equipment, acquisitions — with the outcome you expect and when to review it." testId="decisions-empty" />}
      </PageState>}
      {edit && <DecisionDialog initial={edit} ents={state.data?.ents || []} onClose={() => setEdit(null)} reload={state.reload} />}
      {checkin && <CheckinDialog d={checkin} onClose={() => setCheckin(null)} reload={state.reload} />}
    </div>
  );
}
